import { ReactNode, useContext } from "react";
import dynamic from "next/dynamic";
import {
    ArticlePreviewResponse,
    ArticleSearchResultResponse,
    Collection,
    ProfileView,
    PublicCollectionResponse,
    UserProfileStyle
} from "@authory-packages/core";
import { V3ProfileListItem } from "../V3ProfileListItem/V3ProfileListItem";
import { getCollapseBreakpoint } from "../V3ProfileListItem/V3ProfileListItem.utils";
import { V3ProfileListContainer, V3ProfileListWrapper } from "./V3ProfileList.styles";
import { V3Loader } from "../Loader";
import { VerticalSpacing } from "../VerticalSpacing";
import { V3PinnedProfileItem } from "../V3PinnedProfileItem";
import { getItemUrl, getResponsiveBreakpoint, ItemUrlContext } from "./V3ProfileList.utils";
import { isMainDomain } from "../../utils/domainRouting";
import { RequestHost } from "../../types/headers";
import { DomainContext } from "../../context/domainContext";

const V3ProfileMasonry = dynamic(() => import("./V3ProfileMasonry").then((mod) => mod.V3ProfileMasonry), { ssr: false });

export interface V3ProfileListProps {
    items: ArticleSearchResultResponse[];
    viewType?: ProfileView;
    itemsLoading: boolean;
    pinnedItem?: ArticlePreviewResponse | null;
    isB2B?: boolean;
    collection?: Collection | PublicCollectionResponse;
    collectionOrder?: string;
    isPrivateView?: boolean;
    signupSkeleton?: boolean;
    profileStyle?: UserProfileStyle;
    authorSlug: string;
    smartShareUrl?: string;
    context: ItemUrlContext;
    onItemClick?: (id: string) => void;
    renderItemWrapper?: (item: ArticlePreviewResponse, children: ReactNode, index?: number) => ReactNode;
    renderTrailingItems?: (view: ProfileView) => ReactNode;
    masonryTrailingItem?: ReactNode;
}

export const V3ProfileList = ({
    items,
    itemsLoading,
    viewType = ProfileView.TwoColumnThumbnail,
    pinnedItem,
    collection,
    authorSlug,
    isPrivateView = false,
    signupSkeleton = false,
    smartShareUrl,
    profileStyle,
    context,
    onItemClick,
    renderItemWrapper,
    renderTrailingItems,
    masonryTrailingItem
}: V3ProfileListProps) => {
    const isMasonry = viewType === ProfileView.Masonry;
    const breakpoint = getResponsiveBreakpoint(profileStyle);

    // Current Domain
    const requestHostHeader: RequestHost = useContext(DomainContext) ?? RequestHost.MAINDOMAIN;

    /**
     * Wraps an item with the edit controls when the caller provides them.
     * @param item The item being wrapped
     * @param children The rendered item
     * @param index Position in the list, undefined for the pinned item
     * @returns The wrapped item
     */
    function wrapItem(item: ArticlePreviewResponse, children: ReactNode, index?: number) {
        return renderItemWrapper ? renderItemWrapper(item, children, index) : children;
    }

    /**
     * Renders one list item.
     * @param item The item to render
     * @param index Position in the list
     * @param view The view to render the item in
     * @returns The item element
     */
    function renderItem(item: ArticlePreviewResponse, index: number, view: ProfileView) {
        return (
            <div key={index}>
                {wrapItem(
                    item,
                    <V3ProfileListItem
                        item={item}
                        viewType={view}
                        href={getItemUrl(item, authorSlug, context, smartShareUrl, isMainDomain(requestHostHeader), collection)}
                        isPrivateView={isPrivateView}
                        isSignupPreview={signupSkeleton}
                        hideFavicon={view === ProfileView.Masonry}
                        hideContentType={view === ProfileView.Masonry}
                        responsiveBreakpoint={breakpoint}
                        onItemClick={onItemClick}
                    />,
                    index
                )}
            </div>
        );
    }

    return (
        <V3ProfileListContainer>
            {pinnedItem && (
                <VerticalSpacing bottom={60}>
                    {wrapItem(
                        { ...pinnedItem, pinned: true },
                        <V3PinnedProfileItem
                            item={pinnedItem}
                            viewType={isMasonry ? ProfileView.TwoColumnThumbnail : viewType}
                            href={getItemUrl(pinnedItem, authorSlug, context, smartShareUrl, isMainDomain(requestHostHeader), collection)}
                            isPrivateView={isPrivateView}
                            responsiveBreakpoint={breakpoint}
                        />
                    )}
                </VerticalSpacing>
            )}
            <V3ProfileListWrapper $viewType={viewType} $breakpoint={getCollapseBreakpoint(viewType, breakpoint)}>
                {!isMasonry ? (
                    <>
                        {items.flatMap((page) => page.articles).map((item, index) => renderItem(item, index, viewType))}
                        {renderTrailingItems && renderTrailingItems(viewType)}
                    </>
                ) : (
                    <V3ProfileMasonry
                        items={items}
                        breakpoint={breakpoint}
                        isPrivateView={isPrivateView}
                        profileStyle={profileStyle}
                        renderItem={renderItem}
                        renderTrailingItems={renderTrailingItems}
                        masonryTrailingItem={masonryTrailingItem}
                    />
                )}
            </V3ProfileListWrapper>
            {itemsLoading && (
                <VerticalSpacing top={32} bottom={32} style={{ minHeight: items.length === 0 ? 400 : undefined }}>
                    <V3Loader />
                </VerticalSpacing>
            )}
        </V3ProfileListContainer>
    );
};
